import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Upload, message, Image, Space, Popconfirm 
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import API_ENDPOINTS from '../config/api';

const { TextArea } = Input;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.CLIENTS);
      const result = await response.json();
      if (result.status === 'success') {
        setClients(result.data);
      }
    } catch (error) {
      message.error('Failed to fetch clients');
    }
  };

  const handleAddNew = () => {
    setEditingClient(null);
    setImageUrl('');
    setPdfUrl('');
    setPdfFile(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingClient(record);
    setImageUrl(record.image_url || '');
    setPdfUrl(record.pdf_url || '');
    setPdfFile(null);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      contact: record.contact,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CLIENTS}/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.status === 'success') {
        message.success('Client deleted successfully');
        fetchClients();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to delete client');
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append basic client data
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('contact', values.contact || '');

      // Handle image upload
      if (values.image && values.image[0]) {
        const file = values.image[0].originFileObj;
        formData.append('image', file);
        console.log('Image file:', file);
      }

      // Handle PDF upload
      if (pdfFile) {
        console.log('PDF file being uploaded:', pdfFile);
        formData.append('pdf', pdfFile);
      }

      let url = API_ENDPOINTS.CLIENTS;
      let method = 'POST';

      // If editing, adjust the URL and method
      if (editingClient) {
        url = `${API_ENDPOINTS.CLIENTS}/${editingClient.id}`;
        method = 'POST';
        formData.append('_method', 'PUT');
        formData.append('id', editingClient.id.toString());
      }

      console.log('Request URL:', url);
      console.log('Request Method:', method);

      // Log the FormData contents
      for (let pair of formData.entries()) {
        console.log('FormData entry:', pair[0], ':', pair[1]);
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
      });

      const result = await response.json();
      console.log('API Response:', result);
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to save client');
      }
      
      if (result.status === 'success') {
        message.success(editingClient ? 'Client updated successfully' : 'Client created successfully');
        setIsModalVisible(false);
        form.resetFields();
        setImageUrl('');
        setPdfUrl('');
        setPdfFile(null);
        fetchClients();
      } else {
        throw new Error(result.message || 'Failed to save client');
      }
    } catch (error) {
      console.error('Error saving client:', error);
      message.error(error.message || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image_url',
      key: 'image',
      render: (image_url) => (
        <Image
          src={image_url}
          alt="Client"
          width={50}
          height={50}
          style={{ objectFit: 'cover' }}          
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      render: (text) => text || '-',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'PDF',
      dataIndex: 'pdf_url',
      key: 'pdf',
      render: (pdf_url) => (
        pdf_url ? (
          <a href={pdf_url} target="_blank" rel="noopener noreferrer">View PDF</a>
        ) : '-'
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this client?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <h1>Clients</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
          Add New Client
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={clients}
        rowKey="id"
      />

      <Modal
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Client Name"
            rules={[{ required: true, message: 'Please input client name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="contact"
            label="Contact Info"
          >
            <Input placeholder="Email or phone number" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="image"
            label="Client Image"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
            >
              {imageUrl ? (
                <img src={imageUrl} alt="client" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload Image</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="pdf"
            label="Client PDF"
          >
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                console.log('PDF selected:', file);
                setPdfFile(file);
                return false;
              }}
              onRemove={() => {
                setPdfFile(null);
                setPdfUrl('');
              }}
              accept=".pdf"
              fileList={pdfFile ? [{ uid: '-1', name: pdfFile.name }] : []}
            >
              <Button icon={<UploadOutlined />}>
                {pdfUrl || pdfFile ? 'Replace PDF' : 'Upload PDF'}
              </Button>
            </Upload>
            {pdfUrl && (
              <div style={{ marginTop: 8 }}>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">View Current PDF</a>
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingClient ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Clients;